/**
 * The two coalesced media resolvers: helmet captures, batched per device, and alert snapshots,
 * batched per tenant.
 *
 * A gallery — and, since alarm photos joined the alert log (#1751), an alert log — used to send one
 * resolve per tile: dozens of Lambda invocations, each with its own caller lookup and its own
 * database connections, the moment the view opened. An alert log of ~50 photos was ~50 concurrent
 * `POST /robots/snapshots/resolve` calls, the burst that filled dev's database connection limit on
 * 2026-10-23. Tiles still call `resolveMediaUrl` one at a time; the machinery in
 * `media-resolve-batcher.ts` collects what arrives within one short window and sends it as
 * `POST /robots/media/resolve-batch` or `POST /robots/snapshots/resolve-batch`, retrying a failed
 * chunk as one request per round.
 *
 * GROUPED by what each backend gate checks. A helmet batch names one `robotId` and the server checks
 * every ref against it, so helmet keys batch per device. A snapshot belongs to the tenant — its gate
 * takes no device — so every snapshot key shares one group.
 *
 * The two kinds never mix: each has its own batcher and its own route, which is the folder split
 * `robot-media-ref.ts` depends on. Each also learns on its own that its batch route is missing, so a
 * console deployed ahead of its backend falls back per route — on-prem only: on cloud the browser
 * cannot see that a route is missing (see `isMissingRoute` in `media-resolve-batcher.ts`).
 */

import { robotsApi } from '../../../services/api/domains/robots.api'
import { createResolveBatcher } from './media-resolve-batcher'

export { MAX_BATCH_REFS } from './media-resolve-batcher'

/** The one group every snapshot ref joins: the snapshot gate is the caller's tenant, nothing finer. */
const TENANT_GROUP = 'tenant'

const helmetBatcher = createResolveBatcher({
  single: (ref, robotId) => robotsApi.resolveMedia(ref, robotId).then(res => res.data.url ?? null),
  batch: (refs, robotId) => robotsApi.resolveMediaBatch(robotId, refs).then(res => res.data.urls ?? {}),
})

const snapshotBatcher = createResolveBatcher({
  single: ref => robotsApi.resolveSnapshot(ref).then(res => res.data.url ?? null),
  batch: refs => robotsApi.resolveSnapshotBatch(refs).then(res => res.data.urls ?? {}),
})

/**
 * Resolve one helmet key, sharing a request with every other key for the same device asked for in
 * the same moment. Without a device it cannot be batched and goes out alone. Rejects with the final
 * transport error once retries are spent, so the caller can tell transient from definitive.
 */
export function requestHelmetUrl(ref: string, robotId?: string): Promise<string | null> {
  return helmetBatcher.request(ref, robotId || undefined)
}

/**
 * Resolve one snapshot or gallery key, sharing a request with every other snapshot key asked for in
 * the same moment. Rejects like `requestHelmetUrl`.
 */
export function requestSnapshotUrl(ref: string): Promise<string | null> {
  return snapshotBatcher.request(ref, TENANT_GROUP)
}

/** Test-only: drop anything queued, stop sleeping retries and forget a learned missing route. */
export function __resetMediaBatchForTests(): void {
  helmetBatcher.reset()
  snapshotBatcher.reset()
}
